const LARGE_IMAGE_LONGEST_SIDE = 1000;
const LARGE_IMAGE_QUALITY = 90;

export const getOptimizedImageUrl = (image, options = {}) => {
    const baseUrl = image?.fields?.file?.url;
    if (!baseUrl) return '';

    const url = baseUrl.startsWith('http') ? baseUrl : `https:${baseUrl}`;

    const params = new URLSearchParams();

    // Determine if we're on mobile based on screen width
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const config = isMobile ? (options.mobile || {}) : (options.desktop || {});

    if (config.width) params.append('w', config.width);
    if (config.height) params.append('h', config.height);
    if (config.quality) params.append('q', config.quality);
    if (config.fit) params.append('fit', config.fit);
    params.append('fm', 'webp'); // Use WebP format for better compression

    return `${url}?${params.toString()}`;
};

export const getThumbnailUrl = (image, options = {}) => {
    // Get original dimensions to determine orientation
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    let thumbnailConfig;

    if (originalWidth && originalHeight) {
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 85,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 85,
                    fit: 'fill'
                }
            };
        } else if (isLandscape) {
            // For landscapes: constrain by width
            thumbnailConfig = {
                mobile: {
                    width: 200,
                    quality: 85,
                    fit: 'fill'
                },
                desktop: {
                    width: 400,
                    quality: 85,
                    fit: 'fill'
                }
            };
        } else {
            // For square images: use height constraint
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 85,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 85,
                    fit: 'fill'
                }
            };
        }
    } else {
        // Fallback to original config
        thumbnailConfig = {
            mobile: {
                height: 200,
                quality: 85,
                fit: 'fill'
            },
            desktop: {
                height: 400,
                quality: 85,
                fit: 'fill'
            }
        };
    }

    return getOptimizedImageUrl(image, { ...thumbnailConfig, ...options });
};

// Returns a DPR-aware optimized URL for either a Contentful asset object or a direct URL string.
export const getDprImageUrl = (source, baseMax = 750, opts = {}) => {
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const maxPx = Math.ceil(baseMax * Math.min(dpr, 3));

    // If it's a Contentful asset object with dimensions, pick width/height based on orientation
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) return '';

    const url = fileUrl.startsWith('http') ? fileUrl : `https:${fileUrl}`;

    const params = new URLSearchParams();
    const originalWidth = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width;
    const originalHeight = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height;

    if (originalWidth && originalHeight) {
        if (originalWidth >= originalHeight) {
            params.set('w', String(Math.min(originalWidth, maxPx)));
        } else {
            params.set('h', String(Math.min(originalHeight, maxPx)));
        }
    } else {
        // Fallback when dimensions are unknown: constrain by width
        params.set('w', String(maxPx));
    }

    params.set('fm', opts.format || 'webp');
    params.set('q', String(opts.quality || 85));
    if (opts.fit) params.set('fit', opts.fit);

    // Preserve existing query parameters if any
    const hasQuery = url.includes('?');
    if (hasQuery) {
        const [base, existing] = url.split('?');
        const existingParams = new URLSearchParams(existing);
        // Do not overwrite explicit existing sizing params
        for (const [k, v] of existingParams.entries()) {
            if (!params.has(k)) params.set(k, v);
        }
        return `${base}?${params.toString()}`;
    }
    return `${url}?${params.toString()}`;
};

export function getLargeImageUrl(source, opts = {}) {
    // Get device pixel ratio (capped at 3 for performance)
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const maxPx = Math.ceil(LARGE_IMAGE_LONGEST_SIDE * Math.min(dpr, 3));

    // Extract file URL from Contentful asset object or use direct URL string
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) return '';

    const url = fileUrl.startsWith('http') ? fileUrl : `https:${fileUrl}`;

    // Get original dimensions from Contentful asset
    const originalWidth = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width;
    const originalHeight = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height;

    // Check if the file is a GIF
    const isGif = fileUrl.toLowerCase().endsWith('.gif');

    const params = new URLSearchParams();

    if (originalWidth && originalHeight) {
        // Determine orientation and set appropriate dimension constraint
        if (originalHeight > originalWidth) {
            // Portrait: height should be 600 * DPR
            params.set('h', String(maxPx));
        } else {
            // Landscape or square: width should be 600 * DPR
            params.set('w', String(maxPx));
        }
    } else {
        // Fallback when dimensions are unknown: constrain by width
        params.set('w', String(maxPx));
    }

    // Set format and quality
    params.set('fm', opts.format || (isGif ? 'gif' : 'jpg'));
    params.set('q', String(opts.quality || LARGE_IMAGE_QUALITY));

    // Optional fit parameter
    if (opts.fit) params.set('fit', opts.fit);

    return `${url}?${params.toString()}`;
}

export function getSmallImageUrl(source, opts = {}) {
    // Get device pixel ratio (capped at 3 for performance)
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const baseLongestSide = 300;
    const maxPx = Math.ceil(baseLongestSide * Math.min(dpr, 3));

    // Extract file URL from Contentful asset object or use direct URL string
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) return '';

    const url = fileUrl.startsWith('http') ? fileUrl : `https:${fileUrl}`;

    // Get original dimensions from Contentful asset
    const originalWidth = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width;
    const originalHeight = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height;

    // Check if the file is a GIF
    const isGif = fileUrl.toLowerCase().endsWith('.gif');

    const params = new URLSearchParams();

    if (originalWidth && originalHeight) {
        // Determine orientation and set appropriate dimension constraint
        if (originalHeight > originalWidth) {
            // Portrait: height should be 600 * DPR
            params.set('h', String(maxPx));
        } else {
            // Landscape or square: width should be 600 * DPR
            params.set('w', String(maxPx));
        }
    } else {
        // Fallback when dimensions are unknown: constrain by width
        params.set('w', String(maxPx));
    }

    // Set format and quality
    params.set('fm', opts.format || (isGif ? 'gif' : 'jpg'));
    params.set('q', String(opts.quality || 35));

    // Optional fit parameter
    if (opts.fit) params.set('fit', opts.fit);

    return `${url}?${params.toString()}`;
}

